import { useState, useEffect } from "react";
import * as t from "io-ts";
import { fold } from "fp-ts/lib/Either";

export const saveToLocalStorage = <T>(key: string, value: T) => {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
};

export const getFromLocalStorage = (key: string) =>
  window?.localStorage?.getItem(key);

/**
 * A local storage hook with error handling using io-ts decoders.
 * @param key key to the localStorage value.
 * @param initialValue default value, if the decoder fails, or if the value is not found, this value is returned.
 * @param codec An io-ts codec, if you're storing JSON, please use the "json-from-string" utility.
 */
export default function useLocalStorage<T>(
  key: string,
  initialValue: T,
  codec: t.Type<T, unknown, unknown>
): [T, (arg0: T | ((state: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = getFromLocalStorage(key);
    if (!item) return initialValue;

    let parsed: unknown;
    try {
      parsed = JSON.parse(item);
    } catch {
      parsed = item;
    }
    const decoded = codec.decode(parsed);
    return fold<t.Errors, T, T>(
      () => {
        return initialValue;
      },
      (r) => r
    )(decoded);
  });

  useEffect(() => {
    saveToLocalStorage(key, storedValue);
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}
